<template>
	<b-modal v-model="show" id="print-dispatch-summary" :title="title" size="xl" ref="modal"
		:cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons" :no-close-on-backdrop="true">
		<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

		<b-container v-show="selDispatch !== null">

			<div class="page-container" id="dispatch-summary-container">
				<!-- Dispatcher's Copy -->
				<div class="dispatch-summary">
					<DispatchSummaryHeader :selDispatch="selDispatch" />

					<!-- Source and Destination -->
					<DispatchSummarySourceAndDestination :selDispatch="selDispatch" :sourceLocation="sourceLocation"
						:destinationLocation="destinationLocation" />

					<!-- Asset Types -->
					<DispatchSummaryAssetTypes :selDispatch="selDispatch" :assetsObj="assetsObj" />

					<!-- Remarks -->
					<b-row class="px-2 mt-2 mb-4 remarks-section">
						<b-col sm="12" class="border border-light border-1 p-2">
							<span class="section-header">REMARKS: </span>
							<span class="remarks-value" v-html="remarks"></span>
						</b-col>
					</b-row>

					<!-- Signatories -->
					<DispatchSummarySignatories :selDispatch="selDispatch" :deployedByUser="deployedByUser"
						:receivedByUser="receivedByUser" />

					<!-- Footer -->
					<DispatchSummaryFooter copyOwner="Dispatcher's" />
				</div>

				<!-- Divider Line -->
				<div class="divider-container">
					<i class="fa fa-scissors divider-icon"></i>
					<hr class="divider-line" />
				</div>

				<!-- Receiver's Copy -->
				<div class="dispatch-summary">
					<DispatchSummaryHeader :selDispatch="selDispatch" />

					<!-- Source and Destination -->
					<DispatchSummarySourceAndDestination :selDispatch="selDispatch" :sourceLocation="sourceLocation"
						:destinationLocation="destinationLocation" />

					<!-- Asset Types -->
					<DispatchSummaryAssetTypes :selDispatch="selDispatch" :assetsObj="assetsObj" />

					<!-- Remarks -->
					<b-row class="px-2 mt-2 mb-4 remarks-section">
						<b-col sm="12" class="border border-light border-1 p-2">
							<span class="section-header">REMARKS: </span>
							<span class="remarks-value" v-html="remarks"></span>
						</b-col>
					</b-row>

					<!-- Signatories -->
					<DispatchSummarySignatories :selDispatch="selDispatch" :deployedByUser="deployedByUser"
						:receivedByUser="receivedByUser" />

					<!-- Footer -->
					<DispatchSummaryFooter copyOwner="Receiver's" />
				</div>
			</div>

		</b-container>

		<template #modal-footer>
			<div class="w-100">
				<span class="float-right">
					<b-button variant="secondary" @click="show = false" class="footer-button">
						Close
					</b-button>
					<b-button variant="primary" @click="confirmPrintDispatch" class="footer-button">
						Print
					</b-button>
				</span>
			</div>
		</template>

		<ConfirmPrintDispatch :selDispatch="selDispatch" @onConfirmPrintDispatch="printDispatch" />
	</b-modal>
</template>

<script>
// Components
import DispatchSummaryHeader from './summary/DispatchSummaryHeader';
import DispatchSummarySourceAndDestination from './summary/DispatchSummarySourceAndDestination';
import DispatchSummaryAssetTypes from './summary/DispatchSummaryAssetTypes';
import DispatchSummaryAssets from './summary/DispatchSummaryAssets';
import DispatchSummarySignatories from './summary/DispatchSummarySignatories';
import DispatchSummaryFooter from './summary/DispatchSummaryFooter';
import ConfirmPrintDispatch from './summary/ConfirmPrintDispatch';

// Util
import { DateUtil } from '@/utils/dateutil';
import { DispatchUtil } from '@/utils/dispatchUtil';

// DAO
import assetsDAO from '@/database/assets';

// Others
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import _ from 'lodash';

export default {
	name: 'print-dispatch-summary',
	components: {
		DispatchSummaryHeader,
		DispatchSummarySourceAndDestination,
		DispatchSummaryAssetTypes,
		DispatchSummaryAssets,
		DispatchSummarySignatories,
		DispatchSummaryFooter,
		ConfirmPrintDispatch,
		Loading,
	},
	props: {
		allStorageLocationsObj: {
			type: Object,
			required: true,
		},
		allUsersObj: {
			type: Object,
			required: true,
		}
	},
	data() {
		return {
			show: false,
			selDispatch: {},
			sourceLocation: {},
			destinationLocation: {},
			deployedByUser: '',
			receivedByUser: '',
			assetsObj: {},

			copies: ['Original'],
			currPage: 0,
			configTimeout: 5000,
			isLoading: false,
		};
	},
	computed: {
		title() {
			return 'Print Dispatch ' + this.selDispatch.dispatchId;
		},
		disableConfirmButtons() {
			return this.isLoading;
		},
		dispatchedAssets() {
			return this.selDispatch['Dispatched Assets'] ? this.selDispatch['Dispatched Assets'].length : 0;
		},
		actualAssets() {
			let dispatch = this.selDispatch ? this.selDispatch : {};
			return dispatch.actualAssets ? dispatch.actualAssets.length : 0;
		},
		remarks() {
			let dispatch = this.selDispatch ? this.selDispatch : {};
			return dispatch.notes ? this.truncateRemarks(dispatch.notes) : '-';
		},
		dispatchId() {
			let dispatch = this.selDispatch ? this.selDispatch : {};
			return dispatch.dispatchId ? dispatch.dispatchId : "-";
		},
	},
	mounted() {
		EventBus.$on('onPrintDispatch', async (selDispatch) => {
			this.onReset();

			if (!_.isEmpty(selDispatch)) {
				this.selDispatch = DispatchUtil.cleanupFields(selDispatch);
				await this.retrieveOtherDetails(selDispatch);
			}

			this.$bvModal.show('print-dispatch-summary');
		});
	},
	methods: {
		async retrieveOtherDetails(selDispatch) {
			// Show loader
			this.isLoading = true;

			let sourceLocId = selDispatch.source.storageLocationId;
			this.sourceLocation = this.allStorageLocationsObj[sourceLocId];

			let destinationLocId = selDispatch.destination.storageLocationId;
			this.destinationLocation = this.allStorageLocationsObj[destinationLocId];

			if (selDispatch.deployedBy && selDispatch.deployedBy.length > 0) {
				this.deployedByUser = this.getNameDisplay(this.allUsersObj[selDispatch.deployedBy]);
			} else {
				this.deployedByUser = this.getNameDisplay(this.allUsersObj[selDispatch.createdBy]);
			}

			if (selDispatch.receivedBy && selDispatch.receivedBy.length > 0) {
				this.receivedByUser = this.getNameDisplay(this.allUsersObj[selDispatch.receivedBy]);
			}

			let assets = this.selDispatch.assets ? this.selDispatch.assets : [];
			let actualAssets = this.selDispatch.actualAssets ? this.selDispatch.actualAssets : [];
			let assetCodes = _.uniq([...assets, ...actualAssets]);
			let assetsObjResult = await assetsDAO.getAssetsByCode(assetCodes);
			this.assetsObj = assetsObjResult[0];

			// Check for loader
			this.isLoading = false;
		},
		confirmPrintDispatch(event) {
			event.preventDefault();
			this.$bvModal.show('confirm-print-dispatch');
		},
		async printDispatch(param) {
			try {
				// show loading indicator
				this.isLoading = true;

				let type = param.type;
				if (type === 'Single Copy') {
					this.copies = ['Original'];
				} else {
					this.copies = ['Original', ...param.copies];
				}

				setTimeout(async () => {
					await this.downloadMultipleCopies(this.copies);
					this.$refs.modal.hide();
					this.onReset();
				}, this.configTimeout);

			} catch (error) {
				console.error('Error occurred:', error);
			}

			// hide loading indicator
			this.isLoading = false;
		},

		// Multiple Copies
			async downloadMultipleCopies(copies) {
				try {
					for (let i = 0; i < copies.length; i++) {
						let copyOwner = copies[i];
						const container = document.getElementById('dispatch-summary-container');
						const filename = this.getFileName(copyOwner);
						const canvas = await html2canvas(container, { scale: 2 });
						const imgData = canvas.toDataURL('image/jpeg', 1.0);
						const pdf = new jsPDF({
							unit: 'mm',
							format: 'a4',
							orientation: 'portrait'
						});

						const pdfWidth = 210;
						const pdfHeight = 297;
						const imgWidth = pdfWidth;
						const imgHeight = (canvas.height * imgWidth) / canvas.width;
						let heightLeft = imgHeight;
						let position = 0;

						pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
						heightLeft -= pdfHeight;

						while (heightLeft > 0) {
							position = heightLeft - imgHeight;
							pdf.addPage();
							pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
							heightLeft -= pdfHeight;
						}

						pdf.save(filename);
					}
				} catch (error) {
				console.error('Error occurred:', error);
			} finally {
				// Hide loading indicator
				this.isLoading = false;
			}
		},
		getFileName(copyOwner) {
			return this.dispatchId + '_' + copyOwner + '_Copy.pdf';
		},

		onReset() {
			this.selDispatch = {};
			this.sourceLocation = {};
			this.destinationLocation = {};
			this.deployedByUser = '';
			this.receivedByUser = '';
			this.assetsObj = {};

			this.copies = ['Original'];
			this.currPage = 0;

			// Check for loader
			this.isLoading = false;
			this.show = true;
		},

		// UTILS
		getNameDisplay(userObj) {
			return userObj ? userObj.firstName + ' ' + userObj.lastName : 'TAWItech Support';
		},
		showFormattedDate(date) {
			return DateUtil.getFormattedDateWithTime(date);
		},
		truncateRemarks(string) {
			if (string.length > 200) {
				let remarks = string.substring(0, 200) + '...';
				return this.breakRemarks(remarks, 148);
			}
			return this.breakRemarks(string, 148);
		},
		breakRemarks(remarks, length) {
			return remarks.length > length ? remarks.replace(new RegExp(`([^\\s]{${length}})`, 'g'), '$1<br>') : remarks;
		}
	},
	beforeDestroy() {
		EventBus.$off('onPrintDispatch');
	},
};
</script>

<style scoped>
.page-container {
	padding-top: 1em;
	padding-left: 1.75em;
	padding-right: 1.75em;
	position: relative;
	max-width: 100wh;
	aspect-ratio: 13 / 9;
	overflow: visible;
}

.section-header {
	text-transform: uppercase;
	color: #122C91;
	font-size: .7rem;
	font-style: normal;
	font-weight: bold;
	line-height: normal;
}

.form-field {
	font-size: small;
	text-align: left;
	text-transform: uppercase;
	color: #4A4A4A;
	font-weight: 400;
	line-height: normal;
}

.form-value {
	font-size: small;
	font-weight: bold;
	text-align: left;
	text-transform: uppercase;
	line-height: small;
	color: #484554;
}

.form-value-uppercase {
	font-size: small;
	text-align: left;
	text-transform: uppercase;
	color: #484554;
	font-weight: 700;
	line-height: normal;
}

.remarks-section {
	height: 6em;
}

.remarks-value {
	font-size: .7rem;
	font-style: italic;
	background-color: #BFDFCD;
	display: inline-block;
}

.footer-button {
	margin-left: 0.3em;
}

.pagination-status {
	padding: 0.7em;
	text-transform: uppercase;
	vertical-align: middle;
}

.divider-container {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: -0.9rem;
	margin-left: -0.5rem;
	margin-right: -0.5rem;
	margin-bottom: 0.5rem;
}

.divider-icon {
	font-size: 0.75rem;
}

.divider-line {
	flex-grow: 1;
	border: 0;
	border-top: 0.1px dashed #1f1f1f;
	margin: 0;
}
</style>
